import { request } from '@/api/apiUtils';
import { createEntityQuery } from '@/data/createEntityQuery';
import type { EntityDefinition, FetcherEntry } from '@/data/types';
import type { CommentableModel } from '@/enums';
import type { IComment, ResponseType } from '@/types';

/** Root: query-key prefix for this entity's slice of the cache (ADR 0010) */
export const COMMENTS_ROOT = ['comments'] as const;

interface CommentsListParams {
    projectId: number;
    commentableId: number[];
    commentableModel: CommentableModel;
}

interface CommentDetailParams {
    projectId: number;
    commentableId: number[];
    commentableModel: CommentableModel;
    id: number;
}

interface CommentsSearchParams {
    projectId: number;
    query: string;
}

const fetchers = {
    list: {
        key: () => [...COMMENTS_ROOT, 'list'] as const,
        fetch: async ({ projectId, commentableId, commentableModel }: CommentsListParams): Promise<IComment[]> => {
            const response: ResponseType<IComment[]> = await request(
                `projects/${projectId}/comments?commentable_${commentableModel}=${commentableId.join('.')}&view=active`,
            );
            return response.data;
        },
    },
    detail: {
        // Cache identity depends only on `id`; the remaining params exist for fetch().
        // Callers invalidating a thread may key({ id }) without the fetch-only fields.
        key: ({ id }: Pick<CommentDetailParams, 'id'>) => [...COMMENTS_ROOT, 'detail', id] as const,
        fetch: async ({ projectId, commentableId, commentableModel, id }: CommentDetailParams): Promise<IComment[]> => {
            const response: ResponseType<IComment[]> = await request(
                `projects/${projectId}/comments?commentable_${commentableModel}=${commentableId.join('.')}&view=active&id=${id}`,
            );
            return response.data;
        },
    },
    search: {
        key: (args: CommentsSearchParams) => [...COMMENTS_ROOT, 'search', args] as const,
        fetch: async ({ projectId, query }: CommentsSearchParams): Promise<IComment[]> => {
            const queryParam = query ? `&query=${encodeURIComponent(query)}` : '';
            const response: ResponseType<IComment[]> = await request(`projects/${projectId}/comments?view=all${queryParam}`);
            return response.data;
        },
    },
    // biome-ignore lint/suspicious/noExplicitAny: required for generic type inference
} satisfies Record<string, FetcherEntry<any, any>>;

export const commentsEntity: EntityDefinition<typeof fetchers> = {
    name: 'comments',
    roots: [COMMENTS_ROOT],
    fetchers,
};

export const useCommentsList = createEntityQuery(commentsEntity, 'list');
export const useCommentDetail = createEntityQuery(commentsEntity, 'detail');
export const useCommentsSearch = createEntityQuery(commentsEntity, 'search');

export type { CommentDetailParams, CommentsListParams, CommentsSearchParams };
